import React, { useEffect, useState } from 'react';
import { plainTextOf } from '../../utils/messageActions';
import { Icon } from '../Icon';

const TYPES = [
    { value: '', label: '모든 종류' },
    { value: 'media', label: '🖼️ 사진·영상' },
    { value: 'file', label: '📎 파일' },
    { value: 'link', label: '🔗 링크' },
];

// 날짜 입력(YYYY-MM-DD)을 내 시간대의 하루 시작/끝 시각으로
const dayBoundary = (value, end) => {
    if (!value) return '';
    const [y, m, d] = value.split('-').map(Number);
    const date = end ? new Date(y, m - 1, d, 23, 59, 59, 999) : new Date(y, m - 1, d, 0, 0, 0, 0);
    return date.toISOString();
};

// 메시지 검색 패널 (채널 · 작성자 · 종류 · 기간 필터)
export function SearchPanel({ channelId, channels, onSearch, onSearchUsers, onSelectResult, onClose }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [scope, setScope] = useState(String(channelId));
    const [author, setAuthor] = useState('');
    const [authors, setAuthors] = useState([]);
    const [type, setType] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    useEffect(() => {
        let alive = true;
        onSearchUsers(scope).then(list => alive && setAuthors(list)).catch(() => alive && setAuthors([]));
        return () => { alive = false; };
    }, [scope, onSearchUsers]);

    const hasFilter = !!(author || type || from || to);
    const search = async () => {
        if (!query.trim() && !hasFilter) return;
        setLoading(true);
        try { setResults(await onSearch(query.trim(), scope, author, { type, from: dayBoundary(from, false), to: dayBoundary(to, true) })); }
        catch { setResults([]); }
        finally { setLoading(false); }
    };
    // 필터를 바꾸면 바로 다시 검색 (검색어 없이 "사진만 보기" 같은 사용도 가능)
    useEffect(() => {
        if (results !== null || type || from || to) search();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope, author, type, from, to]);

    const resetFilters = () => { setAuthor(''); setType(''); setFrom(''); setTo(''); };

    return (
        <div className="search-panel">
            <div className="search-panel-header">
                <input autoFocus type="search" placeholder="메시지 검색..." value={query}
                    onChange={e => setQuery(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') search(); if (e.key === 'Escape') onClose(); }} />
                <button className="search-panel-go" onClick={search}>검색</button>
                <button className="nav-icon-btn" onClick={onClose} aria-label="검색 닫기" title="닫기"><Icon name="x" /></button>
                <select className="search-filter-select" aria-label="검색 채널" value={scope} onChange={e => { setScope(e.target.value); setAuthor(''); }}>
                    <option value="all">모든 채널</option>
                    {channels.map(ch => <option key={ch.id} value={String(ch.id)}># {ch.name.startsWith('_private_') ? ch.description : ch.name}</option>)}
                </select>
                <select className="search-filter-select" aria-label="검색 종류" value={type} onChange={e => setType(e.target.value)}>
                    {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <button className={`search-filter-toggle${showFilters || author || from || to ? ' on' : ''}`} onClick={() => setShowFilters(v => !v)}>
                    ⚙️ 상세 필터{author || from || to ? ' •' : ''}
                </button>
                {showFilters && (
                    <div className="search-filters-more">
                        <select className="search-filter-select" aria-label="검색 사용자" value={author} onChange={e => setAuthor(e.target.value)}>
                            <option value="">모든 사용자</option>
                            {authors.map(u => <option key={u.value} value={u.value}>{u.label}</option>)}
                        </select>
                        <label className="search-date">시작일<input type="date" value={from} max={to || undefined} onChange={e => setFrom(e.target.value)} /></label>
                        <label className="search-date">종료일<input type="date" value={to} min={from || undefined} onChange={e => setTo(e.target.value)} /></label>
                        {hasFilter && <button className="search-filter-reset" onClick={resetFilters}>필터 초기화</button>}
                    </div>
                )}
            </div>
            <div className="search-panel-results">
                {loading && <div className="search-panel-empty">검색 중...</div>}
                {!loading && results?.length === 0 && <div className="search-panel-empty">결과 없음</div>}
                {!loading && results === null && <div className="search-panel-empty">검색어를 입력하거나 필터를 고르세요</div>}
                {!loading && results?.map(msg => (
                    <div key={`${msg.channelId}-${msg.id}`} className="search-panel-item" onClick={() => onSelectResult(msg.channelId, msg.id)}>
                        <div className="search-panel-meta"># {msg.channelName.startsWith('_private_') ? '개인 채널' : msg.channelName} · {msg.username} · {new Date(msg.timestamp).toLocaleString()}</div>
                        <div className="search-panel-text">{plainTextOf(msg).replace(/[*_`~>#]/g, '').substring(0, 200)}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}
